// Team Commissions — per-person earnings table on /financials (Aug 26, 2026).
//
// Greg: "commissions table on the financials board for asm, tl, rm, rs —
// monthly and ytd, to make sure they are making enough." One row per active
// earner, grouped ASM → TL → RM → RS: 12-month sparkline, the last three
// months, YTD, monthly average, and Coming (in-flight money only).
//
// Money discipline: every sales dollar comes through the commission board's
// own groupByPerson engine (owner + split + TL/ASM override lines land on the
// right person, non-collect + canvasser carve-out fees included) and is
// bucketed with businessSnapshot's earningsByMonth — the SAME anchor the
// My Business band uses (commission_paid_at ?? commission_ready_date, ET
// month). RS pool dollars = dealRevenue × 1.5% × live active share, anchored
// on rs_pool_paid_at — mirroring the commission board's RS paid view (shares
// are read live, deliberately not snapshotted, same as the board). RS
// "Coming" counts ONLY in-flight deals (pool approved awaiting payout, or
// commission approved but not yet RM-paid) — legacy pre-flag deals whose RM
// side already paid are settled history, not money coming (review-verified:
// the naive commission_approved gate showed $20K of phantom pool).
//
// Deals + roster arrive as props from the page's single fetchFinancialsRaw
// load, so this section rides the page's realtime refresh instead of
// re-pulling. Page gating (admin/finance/sales_director, minus the Jeremy
// carve-out) is inherited from Financials.tsx.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { TeamMember } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import {
  groupByPersonStored,
  type CanvasserRef,
  type CommissionDeal,
} from "../commissions/CommissionBoard";
import { indexPayments, storedRsPoolLines, type CommissionPaymentRow } from "../../lib/commissionPayments";
import {
  anchorMonth,
  earningsByMonth,
  fetchCanvasserByDeal,
  lastMonthsET,
  myLines,
  todayET,
} from "../../lib/businessSnapshot";
import { dealRevenue, num, type FinDeal } from "../../lib/financialsData";

// 1.5% — matches CommissionBoard's RS_POOL_RATE_LIVE and RSPoolSection's
// RS_POOL_RATE (both file-private, so this is the documented third copy —
// change all three together).
const RS_POOL_RATE = 0.015;
const MONTHS_WINDOW = 12;
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ROLE_TIER: Record<string, number> = { asm: 0, team_lead: 1, restore_manager: 2 };
const ROLE_LABEL: Record<string, string> = {
  asm: "ASM",
  team_lead: "TL",
  restore_manager: "RM",
  roof_specialist: "RS",
  roof_team_manager: "Roof Mgr",
};

interface PersonRow {
  id: string;
  name: string;
  role: string;
  tier: number;
  monthly: number[]; // aligned to the lastMonthsET window, oldest → newest
  ytd: number;
  coming: number;
  hasPool: boolean;
}

interface PoolMember {
  team_member_id: string;
  share: number;
}

function Spark({ series }: { series: number[] }) {
  if (series.length < 2 || series.every((v) => v === 0)) {
    return <span className="text-slate-300 text-[10px]">—</span>;
  }
  const max = Math.max(...series, 1);
  const w = 72, h = 18;
  const pts = series
    .map((v, i) => `${(i / (series.length - 1)) * w},${h - (v / max) * (h - 3) - 1.5}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="text-teal-600 opacity-80" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function TeamCommissionsSection({
  deals,
  members,
  payments,
}: {
  /** The page's fetchFinancialsRaw deals (FIN_DEAL_SELECT ⊇ COMMISSION_DEAL_SELECT). */
  deals: FinDeal[];
  /** The page's active-roster record — groupByPerson needs the FULL map. */
  members: Record<string, TeamMember>;
  /** The page's stored payout ledger (Sep 25, 2026) — paid months read these rows. */
  payments: CommissionPaymentRow[];
}) {
  const [poolMembers, setPoolMembers] = useState<PoolMember[] | null>(null);
  const stored = useMemo(() => indexPayments(payments), [payments]);
  const [canvasserByDeal, setCanvasserByDeal] = useState<Record<string, CanvasserRef> | null>(null);
  const [failed, setFailed] = useState(false);

  // The commission queue/history spine — same membership rule as the board.
  const queue = useMemo(
    () => deals.filter((d) => d.commission_ready_date != null) as unknown as CommissionDeal[],
    [deals],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [poolRes, canv] = await Promise.all([
          supabase.from("rs_pool_members").select("team_member_id, status, share").eq("status", "active"),
          fetchCanvasserByDeal(queue.map((d) => d.id)),
        ]);
        if (cancelled) return;
        // An RLS/network-failed pool read must NOT render as "RS earn nothing".
        if (poolRes.error) throw poolRes.error;
        setPoolMembers(
          (((poolRes.data ?? []) as Array<{ team_member_id: string; share: number | null }>) || [])
            .filter((p) => num(p.share) > 0)
            .map((p) => ({ team_member_id: p.team_member_id, share: num(p.share) })),
        );
        setCanvasserByDeal(canv);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queue]);

  const view = useMemo(() => {
    if (poolMembers === null || canvasserByDeal === null) return null;
    const monthKeys = lastMonthsET(MONTHS_WINDOW); // oldest → newest, ends current
    const monthIdx = new Map(monthKeys.map((m, i) => [m, i]));
    const thisYear = todayET().slice(0, 4);
    const monthsElapsed = Number(todayET().slice(5, 7));

    // Paid months come from the stored ledger where it exists (Sep 25, 2026);
    // pre-ledger payouts are still the live recompute.
    const groups = groupByPersonStored(queue, members, canvasserByDeal, stored);
    const rows = new Map<string, PersonRow>();
    const ensure = (m: TeamMember): PersonRow => {
      let r = rows.get(m.id);
      if (!r) {
        r = {
          id: m.id,
          name: m.full_name,
          role: m.role,
          tier: ROLE_TIER[m.role] ?? 3,
          monthly: monthKeys.map(() => 0),
          ytd: 0,
          coming: 0,
          hasPool: false,
        };
        rows.set(m.id, r);
      }
      return r;
    };

    // Sales lines (RM + split + TL/ASM overrides) for every active sales role.
    for (const m of Object.values(members)) {
      if (!(m.role in ROLE_TIER)) continue;
      const r = ensure(m);
      const es = earningsByMonth(groups, m.id, MONTHS_WINDOW);
      es.rows.forEach((row, i) => {
        r.monthly[i] += row.paid;
      });
      const pending = myLines(groups, m.id)
        .filter((it) => !it.deal.commission_paid && !it.deal.commission_approved)
        .reduce((s, it) => s + it.amount, 0);
      r.coming += es.totalApprovedAwaiting + pending;
    }

    // RS pool. A pool paid through the stored ledger (Sep 25, 2026) uses its
    // per-member rows — what actually went out, even to a member who has since
    // left the pool. Older pools = dealRevenue × 1.5% × live active share,
    // paid anchored on rs_pool_paid_at. Coming = in-flight only: pool approved
    // awaiting payout, or the deal's RM side approved-but-unpaid (legacy
    // RM-paid deals with no pool flags are settled history, never "coming").
    const liveRsDeals: CommissionDeal[] = [];
    for (const d of queue) {
      const storedRs = storedRsPoolLines(d, stored);
      if (!storedRs) { liveRsDeals.push(d); continue; }
      for (const ln of storedRs) {
        const m = members[ln.memberId];
        if (!m) continue;
        const r = ensure(m);
        r.hasPool = true;
        const i = monthIdx.get(anchorMonth(String(ln.paidAt ?? d.rs_pool_paid_at)));
        if (i != null) r.monthly[i] += ln.amount;
      }
    }
    for (const pm of poolMembers) {
      const m = members[pm.team_member_id];
      if (!m) continue; // inactive roster rows never earn silently
      const r = ensure(m);
      r.hasPool = true;
      for (const d of liveRsDeals) {
        const dd = d as unknown as FinDeal;
        const pool = Math.max(0, dealRevenue(dd)) * RS_POOL_RATE * pm.share;
        if (pool <= 0) continue;
        if (d.rs_pool_paid && d.rs_pool_paid_at) {
          const mk = anchorMonth(String(d.rs_pool_paid_at));
          const i = monthIdx.get(mk);
          if (i != null) r.monthly[i] += pool;
        } else if (
          (d.rs_pool_approved && !d.rs_pool_paid) ||
          (d.commission_approved && !d.commission_paid)
        ) {
          r.coming += pool;
        }
      }
    }

    const list = [...rows.values()]
      .map((r) => ({
        ...r,
        ytd: monthKeys.reduce((s, mk, i) => (mk.startsWith(thisYear) ? s + r.monthly[i] : s), 0),
      }))
      .sort((a, b) => a.tier - b.tier || b.ytd - a.ytd);

    const last3 = monthKeys.slice(-3);
    const last3Labels = last3.map((mk) => MONTH_NAMES[Number(mk.slice(5)) - 1]);
    const totals = {
      months: last3.map((mk) => {
        const i = monthIdx.get(mk)!;
        return list.reduce((s, r) => s + r.monthly[i], 0);
      }),
      ytd: list.reduce((s, r) => s + r.ytd, 0),
      coming: list.reduce((s, r) => s + r.coming, 0),
    };
    return { list, monthKeys, monthIdx, last3, last3Labels, monthsElapsed, totals };
  }, [queue, members, poolMembers, canvasserByDeal, stored]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-[15px] font-extrabold">🧾 Team commissions — monthly & YTD</h2>
        <span className="text-[11px] text-slate-500 font-semibold">
          paid = cash out the door · anchored on payout month
        </span>
      </div>

      {failed ? (
        <div className="text-[12px] text-slate-400 py-3">
          Couldn't load commission history — try a refresh.
        </div>
      ) : !view ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] min-w-[860px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-1.5 pr-2">Person</th>
                <th className="py-1.5 pr-2">Role</th>
                <th className="py-1.5 pr-2">12 mo</th>
                {view.last3Labels.map((l) => (
                  <th key={l} className="py-1.5 pr-2 text-right">
                    {l}
                  </th>
                ))}
                <th className="py-1.5 pr-2 text-right">YTD</th>
                <th className="py-1.5 pr-2 text-right" title={`YTD ÷ ${view.monthsElapsed} months elapsed`}>
                  Avg/mo
                </th>
                <th className="py-1.5 text-right" title="In-flight only: approved awaiting payout + pending approval (+ RS pool on in-flight deals)">
                  Coming
                </th>
              </tr>
            </thead>
            <tbody>
              {view.list.map((r) => {
                const avg = r.ytd / Math.max(1, view.monthsElapsed);
                return (
                  <tr key={r.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="py-1.5 pr-2 font-bold whitespace-nowrap">{r.name}</td>
                    <td className="py-1.5 pr-2">
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-extrabold whitespace-nowrap">
                        {ROLE_LABEL[r.role] ?? r.role}
                        {r.hasPool && r.role !== "roof_specialist" ? " +RS" : ""}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2">
                      <Spark series={r.monthly} />
                    </td>
                    {view.last3.map((mk) => {
                      const v = r.monthly[view.monthIdx.get(mk)!];
                      return (
                        <td key={mk} className="py-1.5 pr-2 text-right tabular-nums">
                          {v > 0 ? fmtUSD(v, { short: true }) : <span className="text-slate-300">—</span>}
                        </td>
                      );
                    })}
                    <td className="py-1.5 pr-2 text-right tabular-nums font-extrabold">
                      {fmtUSD(r.ytd, { short: true })}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-slate-600">
                      {fmtUSD(avg, { short: true })}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-teal-700 font-bold">
                      {r.coming > 0 ? fmtUSD(r.coming, { short: true }) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t border-slate-300">
                <td className="py-1.5 pr-2 font-extrabold" colSpan={3}>
                  Total
                </td>
                {view.totals.months.map((v, i) => (
                  <td key={i} className="py-1.5 pr-2 text-right tabular-nums font-extrabold">
                    {fmtUSD(v, { short: true })}
                  </td>
                ))}
                <td className="py-1.5 pr-2 text-right tabular-nums font-extrabold">
                  {fmtUSD(view.totals.ytd, { short: true })}
                </td>
                <td className="py-1.5 pr-2" />
                <td className="py-1.5 text-right tabular-nums font-extrabold text-teal-700">
                  {fmtUSD(view.totals.coming, { short: true })}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="text-[10.5px] text-slate-400 mt-2">
            Payouts made since Sep 25, 2026 are the recorded amounts from the payout ledger (what actually
            went out, anchored on the payout date — the same rows Books receives). Earlier payouts run through
            the commission board's engine (splits, TL/ASM overrides, non-collect and canvasser carve-outs
            included) and reconcile with Finance → Commissions to the penny, but re-compute from today's roster
            and rates. RS pool: recorded per-member rows since Sep 25; earlier = 1.5% × commissionable × current
            active shares. The current month is month-to-date.
          </div>
        </div>
      )}
    </div>
  );
}
